import { setOrgTimezone } from "simplydo/core/dates";
import { setInterpolationDictionary } from "simplydo/core/i18n";
import type { OpenAPI } from "simplydo/interfaces";
import type {
  UserMe,
  UserMeOwnerOrganisation,
} from "simplydo/interfaces/types/userTypes";

import util from "utils/utils";

export type { UserMe } from "simplydo/interfaces/types/userTypes";

export type UserAction =
  | { type: "RECEIVE_USER"; user: Partial<UserMe> }
  | { type: "LOGOUT" }
  | { type: "CREATE_GROUP"; id: string; name: string }
  | { type: "JOIN_GROUP"; id: string; name: string }
  | { type: "LEAVE_GROUP"; id: string }
  | {
      type: "UPDATE_TERMS_AGREEMENT";
      legalAgreements: UserMe["legalAgreements"];
    }
  | { type: "UPDATE_PROFILE"; profile: Partial<NonNullable<UserMe["profile"]>> }
  | { type: "UPDATE_TAGS"; tags: OpenAPI.GET<"/tags">["response"] }
  | { type: "UPDATE_PUBLIC_IDEAS" }
  | { type: "UPDATE_EMAILS"; emails: UserMe["emails"] }
  | { type: "UPDATE_PHONE_NUMBERS"; phoneNumbers: UserMe["phoneNumbers"] }
  | {
      type: "TOGGLE_EVENT_SUBSCRIPTION";
      subscriptions: UserMe["eventSubscriptions"];
    }
  | { type: "SWITCH_ORGANISATION"; organisation: UserMeOwnerOrganisation }
  | { type: "UPDATE_ORGANISATION"; field: string; value: unknown }
  | { type: "ADD_ENGAGEMENT"; engagement: string }
  | { type: "UPDATE_PERMISSIONS"; scope: string; permissions: string[] }
  | { type: "UPDATE_ENABLED_SERVICES"; services: UserMe["enabledServices"] }
  | { type: "UPDATE_EMAIL_ADDRESS"; email: string }
  | { type: "UPDATE_FEATURE_FLAGS"; featureFlags: UserMe["featureFlags"] }
  | { type: "UPDATE_TABLE_PREFS"; key: string; preferences: unknown }
  | { type: "GHOST"; id: string }
  | { type: "UNGHOST" }
  | { type: "HIDE_DASHBOARD_TEXT" }
  | { type: "SHOW_DASHBOARD_TEXT" }
  | { type: "HIDE_SECURITY_RECOMMENDATIONS" }
  | { type: "ENABLE_MFA" }
  | { type: "DISABLE_MFA" }
  | { type: "ON_FOLLOW"; context: string; id: string; manual?: boolean }
  | { type: "ADD_IDEA_ID"; ideaId: string }
  | { type: "ADD_COMPANY"; company: NonNullable<UserMe["companies"]>[number] }
  | { type: "TRACK_ONBOARDING_COMPLETION" };

const initialState = null as UserMe | null;

const mergeUser = (state: UserMe | null, patch: Partial<UserMe>): UserMe =>
  ({ ...(state as UserMe), ...patch }) as UserMe;

function user(state = initialState, action: UserAction): UserMe | null {
  switch (action.type) {
    case "RECEIVE_USER":
      setInterpolationDictionary(action.user?.ownerOrganisation?.terminology);
      setOrgTimezone(action.user?.ownerOrganisation?.defaultTimezone);
      return mergeUser(state, action.user);
    case "LOGOUT":
      return null;
    case "CREATE_GROUP": {
      if (!state) return state;
      const existingGroups = Object.assign([], state.groups || []);
      existingGroups.push({ _id: action.id, name: action.name });
      const existingPermissions = { ...state.permissions };
      if (!existingPermissions[action.id]) {
        existingPermissions[action.id] = [];
      }
      existingPermissions[action.id]!.push("group.editSettings");
      return mergeUser(state, {
        groups: existingGroups,
        permissions: existingPermissions,
      });
    }
    case "JOIN_GROUP": {
      if (!state) return state;
      const existingGroups = Object.assign([], state.groups || []);
      existingGroups.push({ _id: action.id, name: action.name });
      return mergeUser(state, {
        groups: existingGroups,
        groupsAvailable: true,
      });
    }
    case "LEAVE_GROUP": {
      if (!state) return state;
      const existingPermissions = { ...state.permissions };
      return mergeUser(state, {
        groups: (state.groups || []).filter((g) => g._id !== action.id),
        permissions: { ...existingPermissions, [action.id]: [] },
      });
    }
    case "UPDATE_TERMS_AGREEMENT": {
      return mergeUser(state, { legalAgreements: action.legalAgreements });
    }
    case "UPDATE_PROFILE": {
      if (!state) return state;
      return mergeUser(state, {
        profile: { ...state.profile, ...action.profile },
      });
    }
    case "UPDATE_TAGS": {
      return mergeUser(state, {
        tags: action.tags.map((t) => t._id),
        ownerTags: action.tags,
      });
    }
    case "UPDATE_PUBLIC_IDEAS": {
      if (!state) return state;
      return mergeUser(state, {
        showIdeasOnProfile: !state.showIdeasOnProfile,
      });
    }
    case "UPDATE_EMAILS": {
      return mergeUser(state, { emails: action.emails });
    }
    case "UPDATE_PHONE_NUMBERS": {
      return mergeUser(state, { phoneNumbers: action.phoneNumbers });
    }
    case "TOGGLE_EVENT_SUBSCRIPTION": {
      return mergeUser(state, {
        eventSubscriptions: action.subscriptions || {},
      });
    }
    case "SWITCH_ORGANISATION": {
      setInterpolationDictionary(action.organisation.terminology);
      setOrgTimezone(action.organisation.defaultTimezone);
      // We temporarily maintain organisation and ownerOrganisation separately
      return mergeUser(state, {
        organisation: action.organisation._id,
        ownerOrganisation: action.organisation,
      });
    }
    case "UPDATE_ORGANISATION": {
      if (!state?.organisation) return state;
      const s = { ...state };
      // Dynamic organisation field updates — ownerOrganisation holds the full object
      const ownerOrganisation = {
        ...s.ownerOrganisation,
        [action.field]: action.value,
      } as UserMeOwnerOrganisation;
      if (action.field === "defaultTimezone") {
        setOrgTimezone(action.value as string);
      }
      return mergeUser(state, { ownerOrganisation });
    }
    case "ADD_ENGAGEMENT": {
      if (!state) return state;
      const { engagements = [] } = state;
      if (!engagements.includes(action.engagement)) {
        const existingEngagements = Object.assign([], engagements || []);
        existingEngagements.push(action.engagement);
        return mergeUser(state, { engagements: existingEngagements });
      }
      return state;
    }
    case "UPDATE_PERMISSIONS": {
      if (!state) return state;
      return mergeUser(state, {
        permissions: {
          ...(state.permissions || {}),
          [action.scope]: action.permissions,
        } as UserMe["permissions"],
      });
    }
    case "UPDATE_ENABLED_SERVICES": {
      return mergeUser(state, { enabledServices: action.services });
    }
    case "UPDATE_EMAIL_ADDRESS": {
      return mergeUser(state, {
        emails: [{ address: action.email, verified: false }],
      });
    }
    case "UPDATE_FEATURE_FLAGS": {
      return mergeUser(state, { featureFlags: action.featureFlags });
    }
    case "UPDATE_TABLE_PREFS": {
      if (!state) return state;
      return mergeUser(state, {
        tablePreferences: {
          ...state.tablePreferences,
          [action.key]: action.preferences,
        } as UserMe["tablePreferences"],
      });
    }
    case "GHOST": {
      return mergeUser(state, { ghostingAs: action.id });
    }
    case "UNGHOST": {
      return mergeUser(state, { ghostingAs: undefined });
    }
    case "HIDE_DASHBOARD_TEXT": {
      return mergeUser(state, { hideDashboardText: true });
    }
    case "SHOW_DASHBOARD_TEXT": {
      return mergeUser(state, { hideDashboardText: false });
    }
    case "HIDE_SECURITY_RECOMMENDATIONS": {
      return mergeUser(state, { hideSecurityRecommendations: true });
    }
    case "ENABLE_MFA": {
      return mergeUser(state, { recommendTwoFactor: false, mfaEnabled: true });
    }
    case "DISABLE_MFA": {
      return mergeUser(state, { recommendTwoFactor: true, mfaEnabled: false });
    }
    case "ON_FOLLOW": {
      if (!state) return state;
      const { context, id, manual } = action;
      const existingFollowing = state.following || [];
      const userIsFollowing = util.userIsFollowingContext(state, context, id);
      const userIsFollowingContextObject = util.userIsFollowingContextObject(
        state,
        context,
        id,
      );
      if (!userIsFollowing) {
        if (
          !userIsFollowingContextObject.unfollowed ||
          (userIsFollowingContextObject.unfollowed && manual)
        ) {
          userIsFollowingContextObject.following = true;
          userIsFollowingContextObject.unfollowed = false;
          const updatedFollowing = [
            userIsFollowingContextObject,
            ...existingFollowing.filter((f) => f._id !== id),
          ];
          return mergeUser(state, { following: updatedFollowing });
        }
      }
      return state;
    }
    case "ADD_IDEA_ID": {
      const { ideaId } = action;
      const existingFollowing = Object.assign([], state?.following || []);
      existingFollowing.push({
        type: "idea",
        _id: ideaId,
        following: true,
        createdAt: new Date().toISOString(),
        reason: "ideaCreated",
      });
      return mergeUser(state, { following: existingFollowing });
    }
    case "ADD_COMPANY": {
      if (!state) return state;
      return mergeUser(state, {
        companies: [...(state.companies || []), action.company],
      });
    }
    case "TRACK_ONBOARDING_COMPLETION": {
      return mergeUser(state, { hasCompletedOrganisationOnboarding: true });
    }
    default:
      return state;
  }
}

export default user;
